import React, { useEffect, useState } from "react";
import Button from "../controls/button";
import CustomPopover from "../popover/Popover";
import CustomButton from "../../components/controls/button/Button";
import {
    Cross,
    PrintIcon,
    ArrowDownMedium,
    MonthIcon,
    QuarterIcon,
    YearIcon,
} from "../../modules/default-svg";
import { Switch } from "@fluentui/react-components";
import styles from "./ReportButton.module.scss";
import projectService from "../../store/services/projects.service";
import programService from "../../store/services/programs.service";
import portfolioService from "../../store/services/portfolio.service";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import { X } from "lucide-react";
import Modal from "../controls/modal/Modal";

const VIEW_MODES = [
    { mode: 2, label: "Month", icon: <MonthIcon /> },
    { mode: 3, label: "Quarter", icon: <QuarterIcon /> },
    { mode: 4, label: "Year", icon: <YearIcon /> },
];

const REPORT_OPTIONS = [
    { key: "IncludeScopes", label: "Scopes" },
    { key: "IncludeTasks", label: "Tasks" },
    { key: "IncludeMilestones", label: "Milestones" },
];

export default function ReportButton({ parentType, parentId }) {
    const [viewMode, setViewMode] = useState(3);
    const [options, setOptions] = useState({
        IncludeScopes: true,
        IncludeTasks: true,
        IncludeMilestones: true,
    });
    const [openPopover, setOpenPopover] = useState(false);
    const [openViewTypePopover, setOpenViewTypePopover] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const dispatch = useDispatch();

    const selectedView = VIEW_MODES.find((v) => v.mode === viewMode);

    const handleOptionToggle = (key, checked) => {
        setOptions((prev) => ({ ...prev, [key]: checked }));
        if(key === "IncludeScopes" && !checked) {
            setOptions((prev) => ({ ...prev, IncludeTasks: false, IncludeScopes: false, }));
        }

        else if(key === "IncludeTasks" && checked && !options.IncludeScopes) {
            setOptions((prev) => ({ ...prev, IncludeTasks: false, IncludeScopes: false, }));
        }
    };

    const handleClose = () => {
        setOpenPopover(false);
        setOpenViewTypePopover(false);
    };

    const handleReport = () => {
        setIsLoading(true);
        const payload = {
            ViewMode: viewMode,
            IncludeScopes: options.IncludeScopes,
            IncludeTasks: options.IncludeTasks,
            IncludeMilestones: options.IncludeMilestones,
        };

        let serviceCall;
        if (parentType === "Project") {
            serviceCall = projectService.ganttReportRequest(parentId, payload);
        } else if (parentType === "Program") {
            serviceCall = programService.ganttReportRequest(parentId, payload);
        } else {
            serviceCall = portfolioService.ganttReportRequest(parentId, payload);
        }

        serviceCall
            .then((response) => {
                const pdfBlob = new Blob([response], { type: "application/pdf" });
                const blobUrl = URL.createObjectURL(pdfBlob);
                setReportData({
                    name: `${parentType} - Timeline Report`,
                    pdfData: blobUrl,
                });
            })
            .catch((error) => {
                console.error("Report generation failed:", error);
                dispatch(
                    generalActions.showToast({
                        title: "Report generation failed. Please try again.",
                        type: "error",
                    })
                );
            })
            .finally(() => {
                setIsLoading(false);
                handleClose();
            });
    };

    useEffect(() => {
        if (!parentType || !parentId) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [parentType, parentId]);

    return (
        <div>
            <CustomPopover
                icon={
                    <Button
                        btnClass={`d_btn ms-12 d_btn_primary ${styles.reportButton}`}
                        icon={<PrintIcon />}
                        label="Report"
                        clickHandler={() => {
                            setOpenViewTypePopover(false);
                            setOpenPopover(true);
                        }}
                    />
                }
                minWidth={320}
                PopSurfaceClass="mt-1"
                status={openPopover}
            >
                <div className="text-[#342c82]">
                    <div className="d-flex justify-content-end align-items-center mb-2">
                        <div onClick={handleClose} style={{ cursor: "pointer" }}>
                            <Cross />
                        </div>
                    </div>
                    <div className={`${styles.filterPopoverBody} FilterPopoverBody`}>
                        <div className="FilterPopoverBodyScroll">
                            <div className="d-flex row align-items-center mb-3">
                                <div className="col-3">View</div>
                                <div className="col-9 align-items-center">
                                    <CustomPopover
                                        icon={
                                            <CustomButton
                                                icon={selectedView.icon}
                                                label={selectedView.label}
                                                btnClass={`${styles.calendarBtn} d_btn`}
                                                rightIcon={
                                                    <span className="ms-6 d-inline-flex">
                                                        <ArrowDownMedium />
                                                    </span>
                                                }
                                                clickHandler={() =>
                                                    setOpenViewTypePopover((prev) => !prev)
                                                }
                                            />
                                        }
                                        width="140px"
                                        PopSurfaceClass={styles.calendarPopover}
                                        status={openViewTypePopover}
                                    >
                                        {VIEW_MODES.map((v) => (
                                            <div
                                                key={v.mode}
                                                className={styles.calendarPopoverItem}
                                                onClick={() => {
                                                    setViewMode(v.mode);
                                                    setOpenViewTypePopover(false);
                                                }}
                                            >
                                                {v.icon}
                                                {v.label}
                                            </div>
                                        ))}
                                    </CustomPopover>
                                </div>
                            </div>
                            <div className={styles.optionsDivider} />
                            {REPORT_OPTIONS.map((opt) => (
                                <div
                                    key={opt.key}
                                    className={`d-flex align-items-center justify-content-between ${styles.optionRow}`}
                                >
                                    <span className={styles.optionLabel}>{opt.label}</span>
                                    <Switch
                                        checked={options[opt.key]}
                                        onChange={(ev, data) =>
                                            handleOptionToggle(opt.key, data.checked)
                                        }
                                        className="ToggleStyle"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="d-flex align-items-end gap-2 justify-content-end FilterPopoverFooter px-[10px]">
                        <CustomButton
                            btnClass="d_btn d_btn_white d_btn_whiteXsm"
                            label="Cancel"
                            icon={<X size={16} />}
                            clickHandler={handleClose}
                        />
                        <CustomButton
                            btnClass="d_btn d_btn_primary d_btn_primaryXsm"
                            label="Generate"
                            icon={<PrintIcon color="#fff" />}
                            clickHandler={handleReport}
                            showLoading={isLoading}
                        />
                    </div>
                </div>
            </CustomPopover>
            {reportData && (
                <Modal
                    title={reportData.name}
                    handleCancel={() => setReportData(null)}
                    body={
                        reportData.pdfData && (
                            <iframe
                                title="PDF Viewer"
                                src={reportData.pdfData}
                                width="100%"
                                height="100%"
                            ></iframe>
                        )
                    }
                    type={true}
                    size=""
                />
            )}
        </div>
    );
}
